import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, ShieldCheck, ChevronRight } from 'lucide-react';
import BrandLogo from './BrandLogo';
import { useLanguage } from '../../context/LanguageContext';
import { PRACTICE_TOPICS } from '../../data/practiceTopics';
import PracticeAreaCatalogModal from './PracticeAreaCatalogModal';
import InstantBookingModal from '../booking/InstantBookingModal';
import { api } from '../../api/client';

export default function Footer() {
  const { currentLang, t } = useLanguage();
  const currentYear = new Date().getFullYear();

  const [catalogModalOpen, setCatalogModalOpen] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedLawyerForBooking, setSelectedLawyerForBooking] = useState(null);
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [lawyers, setLawyers] = useState([]);

  const [primaryOffice, setPrimaryOffice] = useState(null);

  useEffect(() => {
    api.get('/lawyers').then((res) => {
      if (res?.success) setLawyers(res.lawyers || []);
    }).catch(() => {});

    api.get('/offices').then((res) => {
      const list = res?.offices || res?.data?.offices || [];
      if (list.length > 0) setPrimaryOffice(list[0]);
    }).catch(() => {});
  }, []);

  const practiceItems = [
    { id: 'criminal', label: t('topic_criminal', 'Jinoyat ishlari bo‘yicha himoya') },
    { id: 'injury', label: t('topic_injury', 'Fuqarolik va yetkazilgan zarar') },
    { id: 'business', label: t('topic_business', 'Biznes va Korporativ huquq') },
    { id: 'family', label: t('topic_family', 'Oila va meros nizolari') },
    { id: 'estate', label: t('topic_estate', 'Ko‘chmas mulk va investitsiyalar') },
  ];

  const handleOpenPracticeCatalog = (topicId) => {
    const topic = PRACTICE_TOPICS.find((t) => t.id === topicId) || PRACTICE_TOPICS[0];
    setSelectedTopic(topic);
    setCatalogModalOpen(true);
  };

  const firmBio = currentLang === 'uz'
    ? "Barcha sud va tergov bosqichlarida fuqarolar hamda tadbirkorlik subyektlarining huquqlarini professional va ijobiy himoya qilamiz."
    : currentLang === 'ru'
    ? "Профессиональная и бескомпромиссная защита законных прав граждан и бизнеса на всех стадиях следствия и судопроизводства."
    : "We provide strategic legal counsel and aggressive representation to individuals and businesses across all practice areas.";

  const licenseText = currentLang === 'uz'
    ? "O‘zbekiston Respublikasi Advokatlar Palatasi litsenziyasi"
    : currentLang === 'ru'
    ? "Лицензия Палаты Адвокатов Республики Узбекистан"
    : "Licensed by the Chamber of Advocates of the Republic of Uzbekistan";

  const addressText = currentLang === 'uz'
    ? "Jizzax shahar, Islom Karimov ko'chasi, Turon Bank ro'parasi"
    : currentLang === 'ru'
    ? "г. Джизак, улица Ислама Каримова, напротив Турон Банка"
    : "Jizzakh city, Islam Karimov street, opposite Turon Bank";

  const scheduleText = currentLang === 'uz'
    ? "Dush - Shanba: 09:00 - 18:00"
    : currentLang === 'ru'
    ? "Пн - Сб: 09:00 - 18:00"
    : "Mon - Sat: 09:00 - 18:00";

  return (
    <footer className="bg-[#030509] text-slate-400 border-t border-[#141d2d]">
      {/* Upper footer content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          
          {/* Col 1: Law Firm Identity with BrandLogo */}
          <div className="space-y-4">
            <Link to="/" className="inline-block">
              <BrandLogo />
            </Link>

            <p className="text-xs leading-relaxed text-slate-400 font-light">
              {firmBio}
            </p>

            <div className="flex items-center space-x-2 text-[11px] text-slate-400 pt-2 border-t border-[#121926]">
              <ShieldCheck className="w-4 h-4 text-[#c59b27] shrink-0" />
              <span>{licenseText}</span>
            </div>
          </div>

          {/* Col 2: Practice Areas */}
          <div>
            <h3 className="font-serif text-sm font-semibold text-slate-200 uppercase tracking-wider mb-4 border-b border-[#141d2d] pb-2">
              {t('nav_services', 'Amaliyot yo‘nalishlari')}
            </h3>
            <ul className="space-y-2 text-xs">
              {practiceItems.map((item, idx) => (
                <li key={idx}>
                  <button
                    type="button"
                    onClick={() => handleOpenPracticeCatalog(item.id)}
                    className="hover:text-[#c59b27] transition-colors flex items-center text-left cursor-pointer group w-full"
                  >
                    <ChevronRight className="w-3.5 h-3.5 mr-1 text-[#c59b27] group-hover:translate-x-0.5 transition-transform shrink-0" />
                    <span className="group-hover:text-amber-200 transition-colors">{item.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 3: Quick Navigation */}
          <div>
            <h3 className="font-serif text-sm font-semibold text-slate-200 uppercase tracking-wider mb-4 border-b border-[#141d2d] pb-2">
              {currentLang === 'uz' ? 'Navigatsiya' : currentLang === 'ru' ? 'Навигация' : 'Navigation'}
            </h3>
            <ul className="space-y-2 text-xs">
              <li>
                <Link to="/" className="hover:text-[#c59b27] transition-colors">
                  {t('nav_home', 'Bosh sahifa')}
                </Link>
              </li>
              <li>
                <Link to="/about" className="hover:text-[#c59b27] transition-colors">
                  {t('nav_about', 'Advokatlar haqida')}
                </Link>
              </li>
              <li>
                <Link to="/lawyers" className="hover:text-[#c59b27] transition-colors">
                  {t('nav_lawyers', 'Yetakchi Advokatlar')}
                </Link>
              </li>
              <li>
                <Link to="/offices" className="hover:text-[#c59b27] transition-colors">
                  {t('nav_offices', 'Ofislar va Xarita')}
                </Link>
              </li>
              <li>
                <Link to="/check-status" className="hover:text-[#c59b27] transition-colors">
                  {t('nav_check_booking', 'Bron holatini tekshirish')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Col 4: Contact direct info */}
          <div className="space-y-3">
            <h3 className="font-serif text-sm font-semibold text-slate-200 uppercase tracking-wider mb-4 border-b border-[#141d2d] pb-2">
              {t('nav_contact', 'Aloqa')}
            </h3>
            
            <div className="flex items-start space-x-3 text-xs">
              <MapPin className="w-4 h-4 text-[#c59b27] mt-0.5 shrink-0" />
              <a 
                href={
                  primaryOffice?.mapUrl ||
                  primaryOffice?.googleMapsUrl ||
                  (primaryOffice?.latitude && primaryOffice?.longitude
                    ? `https://www.google.com/maps/search/?api=1&query=${primaryOffice.latitude},${primaryOffice.longitude}`
                    : "https://www.google.com/maps/search/?api=1&query=40.160331,67.827246")
                } 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:text-[#c59b27] transition-colors leading-relaxed"
                title="Google Maps xaritasida ofis joylashuvini ko‘rish"
              >
                {(currentLang === 'ru' ? primaryOffice?.addressRu : currentLang === 'en' ? primaryOffice?.addressEn : primaryOffice?.addressUz) || primaryOffice?.address || addressText} ↗
              </a>
            </div>

            <div className="flex items-center space-x-3 text-xs">
              <Phone className="w-4 h-4 text-[#c59b27] shrink-0" />
              <a href={`tel:${primaryOffice?.phone?.replace(/[^+\d]/g, '') || '+998973250770'}`} className="hover:text-[#c59b27] font-mono font-medium">{primaryOffice?.phone || '+998 (97) 325-07-70'}</a>
            </div>

            <div className="flex items-center space-x-3 text-xs">
              <Clock className="w-4 h-4 text-[#c59b27] shrink-0" />
              <span>{scheduleText}</span>
            </div>

            <div className="flex items-center space-x-3 text-xs text-[#c59b27]">
              <ShieldCheck className="w-4 h-4 text-[#c59b27] shrink-0" />
              <span className="font-semibold uppercase tracking-wider text-[11px]">{t('free_consultation_badge', 'Bizda konsultatsiya bepul')}</span>
            </div>
          </div>

        </div>
      </div>

      {/* Bottom copyright */}
      <div className="border-t border-[#101726] bg-[#020306] py-5 px-4 text-center text-[11px] text-slate-500">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between">
          <p>© {currentYear} Jizzax Advokatlari. {t('all_rights_reserved', 'Barcha huquqlar himoyalangan.')}</p>
          <div className="flex flex-wrap justify-center sm:justify-end gap-3 sm:gap-4 mt-2 sm:mt-0">
            <Link to="/terms-of-service" className="hover:text-slate-400">{t('terms_of_service', 'Foydalanish shartlari')}</Link>
            <span>•</span>
            <Link to="/privacy-policy" className="hover:text-slate-400">{t('privacy_policy', 'Maxfiylik siyosati')}</Link>
          </div>
        </div>
      </div>

      {/* Professional Developer Attribution Bar */}
      <div className="border-t border-[#121927] bg-[#010204] py-3.5 px-4 text-xs">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-3 text-slate-400 text-center md:text-left">
          <div className="flex items-center space-x-2">
            <span className="text-[11px] text-slate-500 uppercase tracking-wider">
              {currentLang === 'uz' ? 'Dasturchi va Muallif:' : currentLang === 'ru' ? 'Разработчик и Автор:' : 'Developer & Author:'}
            </span>
            <span className="font-semibold text-slate-200">Abdulazizxon</span>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-4 text-[11px]">
            <a
              href="https://t.me/Abdulazizxon_077"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-slate-300 hover:text-[#229ed9] transition-colors group"
            >
              <svg className="w-3.5 h-3.5 mr-1.5 text-[#229ed9] shrink-0 group-hover:scale-110 transition-transform" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69.01-.03.01-.14-.05-.2-.06-.05-.16-.03-.23-.02-.1.02-1.74 1.11-4.91 3.25-.47.32-.89.48-1.27.47-.42-.01-1.22-.24-1.82-.43-.73-.24-1.31-.37-1.26-.78.03-.21.32-.43.87-.66 3.4-1.48 5.67-2.46 6.81-2.94 3.24-1.35 3.91-1.58 4.36-1.59.1 0 .32.02.46.14.12.1.15.24.16.34.01.07.01.19 0 .34z"/>
              </svg>
              <span className="text-slate-500 mr-1.5">Telegram:</span>
              <span className="font-medium text-[#c59b27] group-hover:underline">@Abdulazizxon_077</span>
            </a>

            <span className="text-slate-700 hidden sm:inline">•</span>

            <a
              href="https://instagram.com/_abdullaev1c_"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-slate-300 hover:text-[#e1306c] transition-colors group"
            >
              <svg className="w-3.5 h-3.5 mr-1.5 text-[#e1306c] shrink-0 group-hover:scale-110 transition-transform" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
              <span className="text-slate-500 mr-1.5">Instagram:</span>
              <span className="font-medium text-[#c59b27] group-hover:underline">_abdullaev1c_</span>
            </a>

            <span className="text-slate-700 hidden sm:inline">•</span>

            <a
              href="tel:+998883254111"
              className="inline-flex items-center text-slate-300 hover:text-[#c59b27] transition-colors group"
            >
              <Phone className="w-3.5 h-3.5 mr-1.5 text-[#c59b27] shrink-0 group-hover:scale-110 transition-transform" />
              <span className="text-slate-500 mr-1.5">Telefon:</span>
              <span className="font-mono font-medium text-slate-200 group-hover:underline">+998 (88) 325-41-11</span>
            </a>
          </div>
        </div>
      </div>

      {/* Practice Area Catalog Modal for Footer clicks */}
      <PracticeAreaCatalogModal
        isOpen={catalogModalOpen}
        onClose={() => setCatalogModalOpen(false)}
        topic={selectedTopic}
        lawyers={lawyers}
        onBookLawyer={(lawyer) => {
          setCatalogModalOpen(false);
          setSelectedLawyerForBooking(lawyer);
          setSelectedTopic({ id: lawyer.specialization, title: lawyer.specialization });
          setBookingModalOpen(true);
        }}
        onBookTopic={(topic) => {
          setCatalogModalOpen(false);
          setSelectedTopic(topic);
          setSelectedLawyerForBooking(null);
          setBookingModalOpen(true);
        }}
      />

      {/* Instant Booking Modal when booked from catalog */}
      <InstantBookingModal
        isOpen={bookingModalOpen}
        onClose={() => {
          setBookingModalOpen(false);
          setSelectedLawyerForBooking(null);
        }}
        preSelectedTopic={selectedTopic}
        initialLawyerId={selectedLawyerForBooking?.id}
      />
    </footer>
  );
}
